/**
 * Dependency Checker - Parse SCAD files for include/use/import dependencies
 * Show actionable warning when include/use/import files are missing
 * @license GPL-3.0-or-later
 */

import { withoutComments } from './scad-comments.js';
import {
  includeStatements,
  computedImportPatterns,
} from './scad-directives.js';

/**
 * Parse SCAD file for dependencies (best-effort regex, not full parser)
 * @param {string} scadContent - OpenSCAD source code
 * @returns {Object} Dependencies found
 */
export function extractDependencies(scadContent) {
  const dependencies = {
    includes: [], // include <file.txt>
    uses: [], // use <file.scad>
    imports: [], // import("file.svg")
    importPatterns: [], // import(str("frog_", n, ".stl")): RegExp sources
  };

  if (!scadContent) return dependencies;

  const source = withoutComments(scadContent);

  for (const { kind, path } of includeStatements(source)) {
    const list = kind === 'include' ? dependencies.includes : dependencies.uses;
    if (!list.includes(path)) list.push(path);
  }
  dependencies.importPatterns = computedImportPatterns(source);

  let match;

  // Match import("filename") - for SVG, STL, etc.
  const importRegex = /import\s*\(\s*["']([^"']+)["']\s*(?:,\s*[^)]+)?\)/g;
  while ((match = importRegex.exec(source)) !== null) {
    const filename = match[1].trim();
    if (filename && !dependencies.imports.includes(filename)) {
      dependencies.imports.push(filename);
    }
  }

  return dependencies;
}

/**
 * Check if all dependencies are present in uploaded files
 * @param {Object} dependencies - From extractDependencies()
 * @param {string[]} uploadedFilenames - Names of uploaded files
 * @param {Object} options - Check options
 * @param {Set<string>} [options.availableLibraries] - Libraries loaded in app
 * @param {string[]} [options.fileParamNames] - Filenames from mounted [file] parameters
 * @returns {Object} Missing files report
 */
export function checkDependencies(
  dependencies,
  uploadedFilenames,
  options = {}
) {
  const { availableLibraries = new Set(), fileParamNames = [] } = options;

  // Combine uploaded files and mounted file-param names into one lookup
  const allAvailableFiles = [...uploadedFilenames, ...fileParamNames];
  const uploadedLower = new Set(allAvailableFiles.map((f) => f.toLowerCase()));
  const uploadedBasenames = new Set(
    allAvailableFiles.map((f) => {
      const parts = f.split('/');
      return parts[parts.length - 1].toLowerCase();
    })
  );

  const missing = {
    includes: [],
    uses: [],
    imports: [],
  };

  // Check includes
  for (const file of dependencies.includes) {
    const lower = file.toLowerCase();
    const basename = lower.split('/').pop();

    // Check if file exists (full path or just basename)
    if (
      !uploadedLower.has(lower) &&
      !uploadedBasenames.has(basename) &&
      !isLibraryPath(file, availableLibraries)
    ) {
      missing.includes.push(file);
    }
  }

  // Check uses
  for (const file of dependencies.uses) {
    const lower = file.toLowerCase();
    const basename = lower.split('/').pop();

    if (
      !uploadedLower.has(lower) &&
      !uploadedBasenames.has(basename) &&
      !isLibraryPath(file, availableLibraries)
    ) {
      missing.uses.push(file);
    }
  }

  // Check imports (SVG, STL, etc.)
  for (const file of dependencies.imports) {
    const lower = file.toLowerCase();
    const basename = lower.split('/').pop();

    if (!uploadedLower.has(lower) && !uploadedBasenames.has(basename)) {
      missing.imports.push(file);
    }
  }

  const hasMissing =
    missing.includes.length > 0 ||
    missing.uses.length > 0 ||
    missing.imports.length > 0;

  return { hasMissing, missing };
}

/**
 * Check if a path refers to a library that's available in the app
 * @param {string} path - File path from include/use
 * @param {Set<string>} availableLibraries - Set of library names
 * @returns {boolean}
 */
function isLibraryPath(path, availableLibraries) {
  // Common OpenSCAD library paths
  const libraryPrefixes = ['MCAD/', 'BOSL/', 'BOSL2/', 'Round-Anything/'];

  for (const prefix of libraryPrefixes) {
    if (path.startsWith(prefix)) {
      const libName = prefix.replace('/', '');
      return availableLibraries.has(libName.toLowerCase());
    }
  }

  return false;
}

/**
 * Recursively scan all SCAD files for dependencies
 * @param {Map<string, string>} files - Map of filename to content
 * @param {string} mainFile - Entry point file
 * @returns {Object} All dependencies found across all files
 */
export function scanAllDependencies(files, mainFile) {
  const allDependencies = {
    includes: new Set(),
    uses: new Set(),
    imports: new Set(),
    importPatterns: new Set(),
  };

  const scanned = new Set();

  function scanFile(filename) {
    if (scanned.has(filename)) return;
    scanned.add(filename);

    const content = files.get(filename);
    if (!content) return;

    const deps = extractDependencies(content);

    // Add to all dependencies
    deps.includes.forEach((f) => allDependencies.includes.add(f));
    deps.uses.forEach((f) => allDependencies.uses.add(f));
    deps.imports.forEach((f) => allDependencies.imports.add(f));
    deps.importPatterns.forEach((p) => allDependencies.importPatterns.add(p));

    // Recursively scan included/used files that exist
    [...deps.includes, ...deps.uses].forEach((depFile) => {
      // Check if this file exists in the uploaded files
      const normalizedPath = normalizeDepPath(depFile, filename);
      if (files.has(normalizedPath)) {
        scanFile(normalizedPath);
      } else if (files.has(depFile)) {
        scanFile(depFile);
      }
    });
  }

  scanFile(mainFile);

  return {
    includes: Array.from(allDependencies.includes),
    uses: Array.from(allDependencies.uses),
    imports: Array.from(allDependencies.imports),
    importPatterns: Array.from(allDependencies.importPatterns),
  };
}

/**
 * Normalize dependency path relative to the file containing it
 * @param {string} depPath - Path from include/use statement
 * @param {string} containingFile - File that has the include/use
 * @returns {string} Normalized path
 */
function normalizeDepPath(depPath, containingFile) {
  // If absolute path (starts with /), return as-is
  if (depPath.startsWith('/')) {
    return depPath.slice(1);
  }

  // Get directory of containing file
  const containingDir = containingFile.includes('/')
    ? containingFile.substring(0, containingFile.lastIndexOf('/'))
    : '';

  if (!containingDir) {
    return depPath;
  }

  // Resolve relative path
  const parts = (containingDir + '/' + depPath).split('/');
  const result = [];

  for (const part of parts) {
    if (part === '.' || part === '') continue;
    if (part === '..') {
      result.pop();
    } else {
      result.push(part);
    }
  }

  return result.join('/');
}

/**
 * Generate user-friendly message for missing dependencies
 * @param {Object} missing - From checkDependencies()
 * @returns {string} Human-readable message
 */
export function formatMissingDependencies(missing) {
  const parts = [];

  if (missing.includes.length > 0) {
    parts.push(`Include files: ${missing.includes.join(', ')}`);
  }
  if (missing.uses.length > 0) {
    parts.push(`Library files: ${missing.uses.join(', ')}`);
  }
  if (missing.imports.length > 0) {
    parts.push(`Import files: ${missing.imports.join(', ')}`);
  }

  return parts.join('\n');
}

/**
 * Create preflight check result for UI display
 * @param {string} scadContent - Main SCAD file content
 * @param {string[]} uploadedFilenames - All uploaded filenames
 * @param {Object} options - Check options
 * @returns {Object} Preflight result
 */
export function runPreflightCheck(
  scadContent,
  uploadedFilenames,
  options = {}
) {
  const dependencies = extractDependencies(scadContent);
  const { hasMissing, missing } = checkDependencies(
    dependencies,
    uploadedFilenames,
    options
  );

  const totalDependencies =
    dependencies.includes.length +
    dependencies.uses.length +
    dependencies.imports.length;

  const totalMissing =
    missing.includes.length + missing.uses.length + missing.imports.length;

  return {
    success: !hasMissing,
    totalDependencies,
    totalMissing,
    dependencies,
    missing,
    message: hasMissing
      ? `Missing ${totalMissing} required file${totalMissing > 1 ? 's' : ''}`
      : totalDependencies > 0
        ? `All ${totalDependencies} dependencies found`
        : 'No external dependencies',
  };
}
